import { useEffect, useCallback, useState, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { API, useListener } from '@unboared/react';
import { Highscore } from '@unboared/lib';
import { Database } from '~/services/db';
import { BINGO_ROOM_ID } from '~/services/db/bingo';
import { ClientDatabase } from '~/types/client';
import { useAuth } from '~/services/auth';
import { useSyncUser } from '~/hooks/models/useUser';
import { useRemoteLaunch } from '~/stores/useRemoteLaunch';
import { useRemoteGameParams } from '~/stores/useRemoteGameParams';
import { useGameParamsFromGame } from '~/stores/useGameParamsFromGame';
import { useRemoteStop } from '~/stores/useRemoteStop';
import { useRemoteGameCommand } from '~/stores/useRemoteGameCommand';
import { usePresentation } from '~/stores/usePresentation';
import { handleAiBridgeMessage } from '~/services/quizStudio/aiBridge';
import { ANALYTICS_CHANGE_EVENT, analyticsBridgeMessage } from '~/services/consent/productAnalytics';
import { trackGameEnd } from '~/utils/gameEndTracking';

const TRANSMIT_CODE = 'unboared::webview::transmit';

/**
 * Bridge between the platform and the WebView game.
 * @param gameName le nom du jeu sous jacent
 * @param src l'url vers le jeu
 * @param clientID the client ID to pass to the game
 * @returns la référence vers le webview, s'il le webview (donc le jeu) est chargé
 */
export const useWebViewPlugin = (webviewRef: any, src: string, clientID?: string, onReady?: () => void) => {
    // Instanciate a reference to the webview
    const [webviewLoaded, setWebviewLoaded] = useState(false);

    // Keep a live ref to the latest onReady so the message listener (registered once
    // with [] deps) never calls a stale closure. Fires on every game's `onReady`
    // handshake, including after a game switch (src change without remount).
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    // Live ref to clientID for the message handler (registered once, [] deps) so
    // the Disco Bingo branches never read a stale/undefined client.
    const clientIDRef = useRef(clientID);
    clientIDRef.current = clientID;

    // Rollout par client de la persistance des playlists IA (re-land de la PR
    // unblind-test#69 après le gel de juillet 2026) : lu depuis
    // `clients/{id}.features.playlistPersistence`. Ref pour le handler `ready`
    // (enregistré une fois, [] deps), state pour l'effet de live-sync.
    const playlistPersistenceRef = useRef(false);
    const [playlistPersistence, setPlaylistPersistence] = useState(false);

    // Abonnement temps réel à l'état du Bingo (proxy durable, gotcha #75). Mis en
    // place à la demande quand le jeu Bingo poste `bingo:subscribe`, nettoyé au
    // changement de jeu / démontage. Seul le Bingo déclenche cet abonnement.
    const bingoUnsubRef = useRef<null | (() => void)>(null);
    const stopBingoSub = () => {
        if (bingoUnsubRef.current) {
            bingoUnsubRef.current();
            bingoUnsubRef.current = null;
        }
    };

    // Get current user info
    const { auth } = useAuth();
    const user = useSyncUser(auth?.uid || 'unknown');

    // Keep a stable reference to the latest user to avoid stale closures in event listeners
    const userRef = useRef(user);
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    // Keep a live reference to the current iframe src so postMessage always targets
    // the CURRENT game origin. When the host switches games, the iframe navigates to a
    // new origin (each game is a separate Firebase Hosting site). `postToWebView` is a
    // stable callback whose listeners are registered once; without this ref it would keep
    // posting to the FIRST game's origin, so the browser silently drops the `ready`
    // handshake to the new game → the new gamepad UI never mounts (white/black/"Connexion…"
    // screen) until a manual refresh. See gotchas: gamepad game-switch handshake.
    const srcRef = useRef(src);
    srcRef.current = src;

    const me = {
        uid: API.getUID(),
        username: API.getUsername(),
        avatar: API.getAvatar(),
        color: API.getColor(),
    };
    const type = API.isScreen() ? 'screen' : 'gamepad';

    // Envoie un message au Webview sous-jacent
    // -- ex : { }
    const postToWebView = useCallback(
        (message: any) => {
            if (!webviewRef.current?.contentWindow) return;
            // Target the CURRENT iframe origin (srcRef), not the one captured at mount —
            // otherwise messages to a newly-switched game are dropped (wrong targetOrigin).
            webviewRef.current.contentWindow.postMessage(message, srcRef.current);
        },
        [webviewRef]
    );

    /**
     * Animator Control (Phase 2A) — relaie l'ordre « Lancer la partie » dans le jeu.
     * L'animateur appuie sur « Lancer » (cockpit) → ScreenApp pousse le nonce dans
     * useRemoteLaunch → ici, on l'injecte dans l'iframe sous la forme d'un message
     * custom `remote-launch`. C'est l'enveloppe qu'un gamepad produit via
     * `broadcast(...)` → chaque jeu déclenche son démarrage en écoutant
     * `API.onMessage('remote-launch', …)` (mécanique réutilisable par le free-play).
     * Nom dédié (≠ 'launch') pour ne JAMAIS entrer en collision avec un message de
     * jeu existant (ex. polyvroom écoute déjà 'launch' côté gamepad).
     *
     * Seul l'écran (host) relaie. Si le jeu n'est pas encore prêt, on attend :
     * l'effet se rejoue quand `webviewLoaded` passe à true. `launchedRef` est
     * initialisé au nonce courant pour ne jamais relancer sur un simple remount.
     */
    const launchNonce = useRemoteLaunch((s) => s.nonce);
    const launchedRef = useRef<number | null>(useRemoteLaunch.getState().nonce);
    useEffect(() => {
        if (type !== 'screen') return;
        if (launchNonce === null || launchNonce === launchedRef.current) return;
        if (!webviewLoaded) return;
        launchedRef.current = launchNonce;
        postToWebView({ action: 'message', data: { message: 'remote-launch', from: 'animator', data: {} } });
    }, [launchNonce, webviewLoaded, type, postToWebView]);

    /**
     * Animator Control (Phase 2B) — relaie les paramètres du jeu réglés par
     * l'animateur (ex. Blind Test : playlist / nb chansons / mode). On poste le
     * message `set-game-params` dans l'iframe : (a) à chaque changement des params,
     * (b) quand le jeu (re)devient prêt (`webviewLoaded`) — pour qu'un jeu fraîchement
     * chargé reçoive les derniers réglages AVANT le lancement. Seul l'écran relaie ;
     * le jeu applique ce qu'il comprend, les autres jeux ignorent ce message.
     */
    const gameParams = useRemoteGameParams((s) => s.params);
    useEffect(() => {
        if (type !== 'screen') return;
        if (!gameParams) return;
        if (!webviewLoaded) return;
        postToWebView({ action: 'message', data: { message: 'set-game-params', from: 'animator', data: gameParams } });
    }, [gameParams, webviewLoaded, type, postToWebView]);

    /**
     * Animator Control (Phase 2D) — relaie l'ordre « Stopper la partie » (retour
     * lobby) dans le jeu. Jumeau du launch : sur un nouveau nonce `remote-stop` (et si
     * l'écran/host + jeu prêt), on poste le message `remote-stop` ; chaque jeu exécute
     * sa propre fonction de retour au lobby via `API.onMessage('remote-stop', …)`.
     */
    const stopNonce = useRemoteStop((s) => s.nonce);
    const stoppedRef = useRef<number | null>(useRemoteStop.getState().nonce);
    useEffect(() => {
        if (type !== 'screen') return;
        if (stopNonce === null || stopNonce === stoppedRef.current) return;
        if (!webviewLoaded) return;
        stoppedRef.current = stopNonce;
        postToWebView({ action: 'message', data: { message: 'remote-stop', from: 'animator', data: {} } });
    }, [stopNonce, webviewLoaded, type, postToWebView]);

    // Accord donné / retiré pendant qu'un jeu tourne (ou PostHog prêt après le jeu) : on renvoie l'état.
    useEffect(() => {
        if (type !== 'screen' || !webviewLoaded) return () => {};
        const relay = () => postToWebView(analyticsBridgeMessage());
        window.addEventListener(ANALYTICS_CHANGE_EVENT, relay);
        return () => window.removeEventListener(ANALYTICS_CHANGE_EVENT, relay);
    }, [webviewLoaded, type, postToWebView]);

    /**
     * Animator Control — relaie une commande de jeu générique envoyée pendant la
     * partie (ex. Unquiz : « question suivante » → message `show-next`). Jumeau du
     * launch/stop : sur un nouveau nonce, on poste le message `name` dans l'iframe ;
     * le jeu exécute les commandes qu'il comprend et ignore les autres.
     */
    const commandName = useRemoteGameCommand((s) => s.name);
    const commandNonce = useRemoteGameCommand((s) => s.nonce);
    const commandRef = useRef<number | null>(useRemoteGameCommand.getState().nonce);
    useEffect(() => {
        if (type !== 'screen') return;
        if (!commandName || commandNonce === null || commandNonce === commandRef.current) return;
        if (!webviewLoaded) return;
        commandRef.current = commandNonce;
        postToWebView({ action: 'message', data: { message: commandName, from: 'animator', data: {} } });
    }, [commandName, commandNonce, webviewLoaded, type, postToWebView]);

    /**
     * Double écran « Régie » — dit au jeu si cette page est l'écran public, pour qu'il
     * cache ses propres commandes (réglages, Lancer…). Renvoyé à chaque jeu prêt (un
     * jeu fraîchement chargé ne connaît rien) et à chaque changement. Jamais envoyé
     * hors écran public : les jeux gardent leur comportement habituel sans ce message.
     */
    const isPublicScreen = usePresentation((s) => s.isPublic);
    const sentPresentationRef = useRef(false);
    useEffect(() => {
        if (type !== 'screen' || !webviewLoaded) return;
        if (!isPublicScreen && !sentPresentationRef.current) return;
        sentPresentationRef.current = true;
        postToWebView({
            action: 'message',
            data: { message: 'set-presentation', from: 'console', data: { public: isPublicScreen } },
        });
    }, [isPublicScreen, webviewLoaded, type, postToWebView]);

    /* Listen from onConnect method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onConnect((deviceID: string) => {
            postToWebView({
                action: 'connect',
                data: {
                    deviceID,
                    data: {
                        state: API.getDeviceState(deviceID),
                        player: API.getPlayer(deviceID),
                    },
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onDisonnect method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onDisconnect((deviceID: string) => {
            postToWebView({
                action: 'disconnect',
                data: { deviceID },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from on method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPlayerChange((deviceID: string) => {
            postToWebView({
                action: 'setPlayer',
                data: {
                    deviceID,
                    player: API.getPlayer(deviceID),
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from on method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onDeviceStateChange((deviceID: string) => {
            postToWebView({
                action: 'setDeviceState',
                data: {
                    deviceID,
                    state: API.getDeviceState(deviceID),
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from on method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onDeviceStatePropertyChange((deviceID: string, key: string) => {
            postToWebView({
                action: 'setDeviceStatePropertyChange',
                data: {
                    deviceID,
                    key,
                    value: API.getDeviceStateProperty(deviceID, key),
                },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onMute method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onModeChange((mode: string) => {
            postToWebView({
                action: 'setMode',
                data: { mode },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onLanguage method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onLanguageChange((language: string) => {
            postToWebView({
                action: 'setLanguage',
                data: { language },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onMute method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onMute((value: boolean) => {
            postToWebView({
                action: 'mute',
                data: { value },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onResume method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onResume(() => {
            postToWebView({ action: 'resume' });
        });
        return unsubscribe;
    }, []);

    /* Listen from onPause method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPause(() => {
            postToWebView({ action: 'pause' });
        });
        return unsubscribe;
    }, []);

    /* Listen from onPersistentDataSaved method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPersistentDataSaved((gameID: string, deviceID: string, key: string) => {
            postToWebView({
                action: 'onPersistentDataSaved',
                data: { gameID, deviceID, key },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onPersistentDataRetrieve method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onPersistentDataRetrieve((gameID: string, deviceID: string, key: string, data: any) => {
            postToWebView({
                action: 'onPersistentDataRetrieve',
                data: { gameID, deviceID, key, data },
            });
        });
        return unsubscribe;
    }, []);

    /* Listen from onHighscoreRetrieve method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onHighscoreRetrieve(
            (gameID: string, deviceID: string, highscore: Highscore, level?: string) => {
                postToWebView({
                    action: 'onHighscoreRetrieve',
                    data: { gameID, deviceID, highscore, level },
                });
            }
        );
        return unsubscribe;
    }, []);

    /* Listen from onHighscoresRetrieve method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onHighscoresRetrieve(
            (gameID: string, highscores: Array<Highscore>, group?: string | Array<string>, level?: string) => {
                postToWebView({
                    action: 'onHighscoresRetrieve',
                    data: { gameID, highscores, group, level },
                });
            }
        );
        return unsubscribe;
    }, []);

    /* Listen from onHighscoreSaved method from parent and delegates to the child */
    useEffect(() => {
        const unsubscribe = API.onHighscoreSaved((gameID: string, deviceID: string, level?: string) => {
            postToWebView({
                action: 'onHighscoreSaved',
                data: { gameID, deviceID, level },
            });
        });
        return unsubscribe;
    }, []);

    // Transmit child level messages without interfering
    // with the parent application. `set-presentation` ne vient que de la console
    // (écran public) : un téléphone qui l'enverrait cacherait les commandes de la TV.
    useListener(
        TRANSMIT_CODE,
        (payload: any) => {
            if (payload?.action === 'message' && payload?.data?.message === 'set-presentation') return;
            postToWebView(payload);
        },
        []
    );

    // Receive messages from the underlying WebView (the child app)
    // and process it
    // -- ex : { }
    useEffect(() => {
        const webviewMessageHandler = (event: any) => {
            const { data } = event;
            if (!data || typeof data !== 'object') return;
            // Les écritures Bingo sont durables et destructrices (reset) : ne les
            // traiter que si le message vient bien de l'iframe du jeu qu'on pilote
            // (pas d'une autre iframe / extension qui posterait un `bingo:*` parasite).
            const fromGame = event.source === webviewRef.current?.contentWindow;
            // Création IA (régie TV UnQuiz) : capacités, génération, annulation.
            if (handleAiBridgeMessage(data, { fromGame, clientID: clientIDRef.current, post: postToWebView })) return;
            if (data.action === 'onReady') {
                setWebviewLoaded(true);
                // Nouveau jeu chargé (switch sans remount) → on coupe un éventuel
                // abonnement Bingo périmé. Le Bingo le rétablit via `bingo:subscribe`.
                stopBingoSub();
                // Notify the screen that the (new) game is live — drives the free-play
                // transition overlay's fade-out at the exact handshake moment.
                onReadyRef.current?.();
                //
                const devices: any[] = [];
                for (const screenID of API.getScreenIDs()) {
                    devices.push({
                        deviceID: screenID,
                        data: {
                            player: API.getPlayer(screenID),
                            state: API.getDeviceState(screenID),
                        },
                    });
                }

                for (const gamepadID of API.getGamepadIDs()) {
                    devices.push({
                        deviceID: gamepadID,
                        data: {
                            player: API.getPlayer(gamepadID),
                            state: API.getDeviceState(gamepadID),
                        },
                    });
                }

                postToWebView({
                    action: 'ready',
                    data: {
                        hostID: API.getHostID(),
                        myDeviceID: API.getDeviceID(),
                        me, // Current user info
                        type, // 'screen' or 'gamepad'
                        devices,
                        session: {
                            id: API.getSessionID(),
                            url: API.getSessionLink(),
                        },
                        mute: API.isMute(),
                        mode: API.getMode(),
                        language: API.getLanguage(),
                        companyId: clientID, // Pass client ID to the game
                        // Feature flags par client (rollout). Si la lecture
                        // Firestore n'a pas encore abouti, un message `features`
                        // séparé suit dès qu'elle se termine.
                        features: { playlistPersistence: playlistPersistenceRef.current },
                        // The establishments the user belongs to are sent
                        // separately via the 'sites' message (with names),
                        // since resolving names is asynchronous.
                    },
                });
                // Réglages de partie (télécommande + mémoire par jeu) : `webviewLoaded`
                // ne repasse jamais à false, donc après un changement de jeu sans
                // remontage l'effet de relais ne se rejoue pas — on les renvoie au jeu
                // qui vient d'annoncer qu'il est prêt (sinon il démarrerait sans eux).
                const remoteParams = useRemoteGameParams.getState().params;
                if (API.isScreen() && remoteParams) {
                    postToWebView({
                        action: 'message',
                        data: { message: 'set-game-params', from: 'animator', data: remoteParams },
                    });
                }
                // Écran public (double écran) : même raison, le jeu qui arrive doit cacher ses commandes.
                if (API.isScreen() && usePresentation.getState().isPublic) {
                    postToWebView({
                        action: 'message',
                        data: { message: 'set-presentation', from: 'console', data: { public: true } },
                    });
                }
                // Enregistrement des sessions (bandeau cookies) : l'écran TV seulement, jamais la
                // manette des joueurs. Le jeu ne démarre PostHog que sur `enabled: true`.
                if (API.isScreen()) postToWebView(analyticsBridgeMessage());
            } else if (data.action === 'send') {
                // console.log(
                //   'Game Runner Gamepad Send delay, ',
                //   Date.now() - data.data?.data?.data?.timestamp
                // );
                API.send(data.to, TRANSMIT_CODE, data.data);
            } else if (data.action === 'broadcast') {
                API.broadcast(TRANSMIT_CODE, data.data);
                // } else if (data.action === 'navigateToHome') {
                //     API.navigateToHome();
                // } else if (data.action === 'navigateTo') {
                //     API.navigateTo(data.data.url);
            } else if (data.action === 'savePersistentData') {
                API.savePersistentData(data.data.gameID, data.data.deviceID, data.data.key, data.data.data);
            } else if (data.action === 'retrievePersistentData') {
                API.retrievePersistentData(data.data.gameID, data.data.deviceID, data.data.key);
            } else if (data.action === 'saveHighscore') {
                API.saveHighscore(data.data.gameID, data.data.deviceID, data.data.score, data.data.level);
                trackGameEnd(clientIDRef.current, data.data.gameID, API.getMode());
            } else if (data.action === 'saveQuiz') {
                if (clientID) {
                    const createdBy = userRef.current?.firstname || undefined;
                    const quizDataWithCreator = {
                        ...data.data.quizData,
                        createdBy,
                    };
                    // The game sends the establishments it wants to write to
                    // (targetClientIDs). We only keep those the user actually
                    // belongs to (never trust ids from the game) and always
                    // include the current establishment. '*' is never a target.
                    const authorizedSites = new Set(
                        (userRef.current?.getSiteIDs?.() ?? []).filter((id) => id !== '*')
                    );
                    const requested = Array.isArray(data.data.targetClientIDs)
                        ? data.data.targetClientIDs
                        : [];
                    const targets = Array.from(
                        new Set([
                            clientID,
                            ...requested.filter((id: string) => authorizedSites.has(id)),
                        ])
                    ).filter(Boolean);
                    // allSettled (not all): a failed propagation to one sibling
                    // establishment must not fail the user's own save. Success is
                    // keyed on the current establishment (clientID); failures on
                    // other sites are logged and reported as a count.
                    Promise.allSettled(
                        targets.map((targetClientID) =>
                            Database.quizzes.saveQuiz(
                                targetClientID,
                                data.data.gameID,
                                data.data.quizId,
                                quizDataWithCreator
                            )
                        )
                    ).then((results) => {
                        let failedCount = 0;
                        results.forEach((result, i) => {
                            if (result.status === 'rejected') {
                                failedCount += 1;
                                console.error(`Error saving quiz on ${targets[i]}:`, result.reason);
                            }
                        });
                        const primaryResult = results[targets.indexOf(clientID)];
                        if (primaryResult?.status === 'fulfilled') {
                            postToWebView({
                                action: 'onQuizSaved',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    // Renvoyé tel quel : le jeu relie chaque réponse à SON envoi.
                                    requestId: data.data.requestId,
                                    propagatedTo: targets.length - failedCount,
                                    failedCount,
                                },
                            });
                        } else {
                            Sentry.captureException(primaryResult?.reason ?? new Error('saveQuiz failed'), {
                                tags: { area: 'quiz_bridge' },
                            });
                            postToWebView({
                                action: 'onQuizSaveError',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    // Renvoyé tel quel : le jeu relie chaque réponse à SON envoi.
                                    requestId: data.data.requestId,
                                    error: primaryResult?.reason?.message || 'save failed',
                                },
                            });
                        }
                    });
                } else {
                    // Défensif (aujourd'hui l'écran passe toujours un clientID) : sans
                    // établissement, répondre quand même — un pont muet laisse le jeu
                    // bloqué sur « Enregistrement… » (perte de 2 h de travail, 15/10).
                    Sentry.captureMessage('saveQuiz sans clientID', {
                        level: 'warning',
                        tags: { area: 'quiz_bridge' },
                    });
                    postToWebView({
                        action: 'onQuizSaveError',
                        data: {
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            requestId: data.data.requestId,
                            error: 'no-client',
                        },
                    });
                }
            } else if (data.action === 'retrieveQuiz') {
                if (clientID) {
                    Database.quizzes
                        .retrieveQuiz(clientID, data.data.gameID, data.data.quizId)
                        .then((quizData) => {
                            postToWebView({
                                action: 'onQuizRetrieve',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, quizData },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error retrieving quiz:', error);
                        });
                }
            } else if (data.action === 'retrieveQuizzes') {
                if (clientID) {
                    Database.quizzes
                        .retrieveQuizzes(clientID, data.data.gameID, data.data.total)
                        .then((quizzes) => {
                            postToWebView({
                                action: 'onQuizzesRetrieve',
                                data: { gameID: data.data.gameID, quizzes },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error retrieving quizzes:', error);
                        });
                }
            } else if (data.action === 'deleteQuiz') {
                if (clientID) {
                    Database.quizzes
                        .deleteQuiz(clientID, data.data.gameID, data.data.quizId)
                        .then(() => {
                            postToWebView({
                                action: 'onQuizDeleted',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error deleting quiz:', error);
                            postToWebView({
                                action: 'onQuizDeleteError',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, error: error.message },
                            });
                        });
                }
            } else if (data.action === 'uploadQuizCoverImage') {
                if (clientID) {
                    Database.quizzes
                        .uploadCoverImageFromDataUrl({
                            clientID,
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            fileName: data.data.fileName,
                            dataUrl: data.data.dataUrl,
                        })
                        .then(({ url, path }) => {
                            postToWebView({
                                action: 'onQuizCoverImageUploaded',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, url, path },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error uploading cover image:', error);
                            postToWebView({
                                action: 'onQuizCoverImageUploadError',
                                data: { gameID: data.data.gameID, quizId: data.data.quizId, error: error.message },
                            });
                        });
                }
            } else if (data.action === 'uploadQuizImage') {
                // Upload générique (couverture OU image de question) : `slot` est
                // renvoyé tel quel pour que le jeu corrèle la réponse à la bonne
                // cible (id de question, ou 'cover'). Aucune écriture Firestore.
                if (clientID) {
                    Database.quizzes
                        .uploadImageFromDataUrl({
                            clientID,
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            folder: data.data.folder,
                            fileName: data.data.fileName,
                            dataUrl: data.data.dataUrl,
                        })
                        .then(({ url, path }) => {
                            postToWebView({
                                action: 'onQuizImageUploaded',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    slot: data.data.slot,
                                    url,
                                    path,
                                },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error uploading quiz image:', error);
                            postToWebView({
                                action: 'onQuizImageUploadError',
                                data: {
                                    gameID: data.data.gameID,
                                    quizId: data.data.quizId,
                                    slot: data.data.slot,
                                    error: error.message,
                                },
                            });
                        });
                } else {
                    // Pas de clientID (ex. gamepad runner) : répondre l'échec plutôt
                    // que d'avaler la requête — le jeu attend une réponse pour
                    // débloquer son UI d'upload.
                    postToWebView({
                        action: 'onQuizImageUploadError',
                        data: {
                            gameID: data.data.gameID,
                            quizId: data.data.quizId,
                            slot: data.data.slot,
                            error: 'no clientID',
                        },
                    });
                }
            } else if (data.action === 'retrieveHighscore') {
                API.retrieveHighscore(data.data.gameID, data.data.deviceID, data.data.level);
            } else if (data.action === 'retrieveHighscores') {
                API.retrieveHighscores(data.data.gameID, data.data.total, data.data.group, data.data.level);
            } else if (data.action === 'bingo:subscribe') {
                // Le jeu Bingo demande l'état faisant foi : on (ré)ouvre l'abonnement
                // au doc durable et on pousse `bingo:state` à chaque changement
                // (y compris l'état courant immédiatement). La console est l'autorité
                // (gotcha #75) : le clientID utilisé est le sien, jamais celui du jeu.
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    stopBingoSub();
                    bingoUnsubRef.current = Database.bingo.subscribe(cid, BINGO_ROOM_ID)((state) => {
                        postToWebView({ action: 'bingo:state', data: state });
                    });
                }
            } else if (data.action === 'bingo:draw') {
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    Database.bingo
                        .draw(cid, BINGO_ROOM_ID, Number(data.data?.number))
                        .catch((error: any) => console.error('Error drawing bingo number:', error));
                }
            } else if (data.action === 'bingo:undo') {
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    Database.bingo
                        .undo(cid, BINGO_ROOM_ID)
                        .catch((error: any) => console.error('Error undoing bingo draw:', error));
                }
            } else if (data.action === 'bingo:reset') {
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    Database.bingo
                        .reset(cid, BINGO_ROOM_ID)
                        .catch((error: any) => console.error('Error resetting bingo:', error));
                }
            } else if (data.action === 'bingo:setMax') {
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    Database.bingo
                        .setMax(cid, BINGO_ROOM_ID, Number(data.data?.maxNumber))
                        .catch((error: any) => console.error('Error setting bingo range:', error));
                }
            } else if (data.action === 'savePlaylist') {
                // Playlists IA (unblind-test) persistées sur le compte client, pour
                // qu'une playlist créée sur l'écran soit lançable depuis le cockpit.
                // Comme le Bingo : le clientID vient de la console (ref, jamais du
                // jeu) et on n'accepte que les messages émis par l'iframe du jeu.
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    const playlistData = {
                        ...data.data.playlistData,
                        createdBy: userRef.current?.firstname || undefined,
                    };
                    Database.playlists
                        .savePlaylist(cid, data.data.gameID, data.data.playlistId, playlistData)
                        .then(() => {
                            postToWebView({
                                action: 'onPlaylistSaved',
                                data: { gameID: data.data.gameID, playlistId: data.data.playlistId },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error saving AI playlist:', error);
                            postToWebView({
                                action: 'onPlaylistSaveError',
                                data: {
                                    gameID: data.data.gameID,
                                    playlistId: data.data.playlistId,
                                    error: error?.message || 'save failed',
                                },
                            });
                        });
                }
            } else if (data.action === 'retrievePlaylists') {
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    Database.playlists
                        .retrievePlaylists(cid, data.data.gameID, data.data.total)
                        .then((playlists) => {
                            postToWebView({
                                action: 'onPlaylistsRetrieve',
                                data: { gameID: data.data.gameID, playlists },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error retrieving AI playlists:', error);
                            postToWebView({
                                action: 'onPlaylistsRetrieveError',
                                data: {
                                    gameID: data.data.gameID,
                                    error: error?.message || 'retrieve failed',
                                },
                            });
                        });
                }
            } else if (data.action === 'deletePlaylist') {
                const cid = clientIDRef.current;
                if (cid && fromGame) {
                    Database.playlists
                        .deletePlaylist(cid, data.data.gameID, data.data.playlistId)
                        .then(() => {
                            postToWebView({
                                action: 'onPlaylistDeleted',
                                data: { gameID: data.data.gameID, playlistId: data.data.playlistId },
                            });
                        })
                        .catch((error: any) => {
                            console.error('Error deleting AI playlist:', error);
                            postToWebView({
                                action: 'onPlaylistDeleteError',
                                data: {
                                    gameID: data.data.gameID,
                                    playlistId: data.data.playlistId,
                                    error: error?.message || 'delete failed',
                                },
                            });
                        });
                }
            } else if (data.action === 'saveGameParams') {
                // Réglages de partie faits sur la TV (Blind Test) : ScreenApp les range
                // sur le compte pour que la télécommande les affiche et qu'ils soient
                // mémorisés. Seulement l'iframe du jeu qu'on pilote, sur l'écran (host).
                if (fromGame && type === 'screen' && data.data && typeof data.data === 'object') {
                    useGameParamsFromGame.getState().push(data.data);
                }
            } else if (data.action === 'debug') {
                console.log('[Debug] Debug message:', data.data);
            }
        };

        //
        window.addEventListener('message', webviewMessageHandler);

        return () => {
            window.removeEventListener('message', webviewMessageHandler);
            stopBingoSub();
        };
    }, []);

    // Suivi TEMPS RÉEL du flag de rollout (déclaré plus haut, près de
    // clientIDRef) : un message `features` part à chaque changement effectif —
    // y compris `false`, pour que couper le flag dans Firestore (kill-switch)
    // atteigne un écran déjà lancé sans redémarrage. Le message séparé est
    // nécessaire car la lecture Firestore peut aboutir APRÈS le handshake
    // `ready`. Sans flag, le jeu reste en localStorage — comportement
    // identique à la prod actuelle.
    useEffect(() => {
        playlistPersistenceRef.current = false;
        setPlaylistPersistence(false);
        if (!clientID) return () => {};

        const unsubscribe = ClientDatabase.subscribeFeature(clientID, 'playlistPersistence', (enabled) => {
            if (enabled === playlistPersistenceRef.current) return;
            playlistPersistenceRef.current = enabled;
            setPlaylistPersistence(enabled);
            postToWebView({ action: 'features', data: { playlistPersistence: enabled } });
        });
        return () => unsubscribe();
    }, [clientID, postToWebView]);

    // Subscribe to quizzes changes for real-time updates
    useEffect(() => {
        if (!clientID) return () => {};

        const unsubscribe = Database.quizzes.subscribeToQuizzes(
            clientID,
            'unquiz'
        )(() => {
            // When quizzes change, send updated data to game
            Database.quizzes
                .retrieveQuizzes(clientID, 'unquiz', 0)
                .then((quizzes) => {
                    postToWebView({
                        action: 'onQuizzesRetrieve',
                        data: { gameID: 'unquiz', quizzes },
                    });
                })
                .catch((error) => {
                    console.error('Error refreshing quizzes after change:', error);
                });
        });

        return () => unsubscribe();
    }, [clientID, postToWebView]);

    // Idem pour les playlists IA du blind test : une playlist créée sur l'écran
    // (ou supprimée depuis le cockpit) doit se refléter sans recharger le jeu.
    // ⚠️ Anti-gel juillet 2026 : débouncé, et on ne pousse au jeu que si le
    // CONTENU a réellement changé depuis le dernier push (pattern « compare et
    // pousse si changé », cf. useScreenPlayerCount). On ne se fie PAS aux
    // métadonnées de snapshot (hasPendingWrites/docChanges) pour décider :
    // leurs subtilités (latence locale d'un write, ack serveur metadata-only
    // d'un delete) rendraient certains changements invisibles. Chaque push
    // superflu relançait côté jeu une cascade de fetches Deezer — la
    // comparaison de contenu les élimine tous, échos et ré-émissions compris.
    const lastPushedPlaylistsRef = useRef<string>('');
    useEffect(() => {
        if (!clientID || !playlistPersistence) return () => {};

        lastPushedPlaylistsRef.current = '';
        let debounceId: ReturnType<typeof setTimeout> | undefined;
        const unsubscribe = Database.playlists.subscribeToPlaylists(
            clientID,
            'unblind-test'
        )(() => {
            clearTimeout(debounceId);
            debounceId = setTimeout(() => {
                Database.playlists
                    .retrievePlaylists(clientID, 'unblind-test', 0)
                    .then((playlists) => {
                        const signature = JSON.stringify(
                            playlists.map((p) => ({ id: p.id, name: p.name, tracks: p.tracks }))
                        );
                        if (signature === lastPushedPlaylistsRef.current) return;
                        lastPushedPlaylistsRef.current = signature;
                        postToWebView({
                            action: 'onPlaylistsRetrieve',
                            data: { gameID: 'unblind-test', playlists },
                        });
                    })
                    .catch((error) => {
                        console.error('Error refreshing AI playlists after change:', error);
                    });
            }, 500);
        });

        return () => {
            clearTimeout(debounceId);
            unsubscribe();
        };
    }, [clientID, playlistPersistence, postToWebView]);

    // Push the user's establishments (with names) to the game once they are
    // loaded. The wildcard '*' (super-admin "all clients") is excluded — it is
    // not a real establishment to target.
    //
    // `user` is a fresh ClientUser instance on every Firestore snapshot, so this
    // effect re-fires often. We cache the resolved sites keyed by the set of ids
    // and only re-fetch when that set actually changes — otherwise we just
    // re-post the cached list (cheap), instead of re-reading every client doc.
    const sitesCacheRef = useRef<{
        key: string;
        sites: { id: string; name: string }[];
    }>({ key: '', sites: [] });
    useEffect(() => {
        if (!webviewLoaded) return;
        const siteIDs = (user?.getSiteIDs?.() ?? []).filter((id) => id !== '*');
        if (siteIDs.length === 0) return;
        const key = siteIDs.slice().sort().join(',');
        if (key === sitesCacheRef.current.key) {
            postToWebView({ action: 'sites', data: { sites: sitesCacheRef.current.sites } });
            return;
        }
        let cancelled = false;
        // Resolve each client independently so one missing/inaccessible
        // establishment falls back to its id without losing every other name.
        Promise.all(
            siteIDs.map((id) =>
                ClientDatabase.getClient(id)
                    .then((client) => ({ id, name: client?.name || id }))
                    .catch(() => ({ id, name: id }))
            )
        ).then((sites) => {
            if (cancelled) return;
            sitesCacheRef.current = { key, sites };
            postToWebView({ action: 'sites', data: { sites } });
        });
        return () => {
            cancelled = true;
        };
    }, [webviewLoaded, user, postToWebView]);

    return { isReady: webviewLoaded };
};
